import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

const getAuthHeaders = () => {
  const token = localStorage.getItem("clerkToken");
  return {
    Authorization: `Bearer ${token}`,
  };
};

/**
 * Fetch paginated private listings (buy signals).
 * GET /private-listings/paginated with query params. Uses same auth as other dealer APIs.
 * @param {Object} params - page, per_page, listing_type, listing_site ("Facebook" | "OfferUp" | "Craigslist" | omit for all), make (or makes[]), ...
 * @returns {Promise<{ listings, total_count, page, per_page, total_pages }>}
 */
export const fetchPrivateListingsPaginated = (params = {}) => {
  const {
    page = 1,
    per_page = 20,
    listing_type = "all",
    listing_site,
    listing_id,
    make,
    makes,
    min_year,
    max_year,
    min_mileage,
    max_mileage,
    min_price,
    max_price,
    radius,
    sort_by = "listing_date",
    sort_order = "desc",
    hide_viewed,
  } = params;

  const searchParams = new URLSearchParams({
    page: String(page),
    per_page: String(per_page),
    listing_type,
    sort_by,
    sort_order,
  });
  if (listing_site === "Facebook" || listing_site === "OfferUp" || listing_site === "Craigslist") searchParams.set("listing_site", listing_site);
  if (listing_id != null && String(listing_id).trim() !== "") searchParams.set("listing_id", String(listing_id).trim());
  const makeVal = make ?? (Array.isArray(makes) && makes.length > 0 ? makes.join(",") : undefined);
  if (makeVal) searchParams.set("make", makeVal);
  if (min_year != null) searchParams.set("min_year", String(min_year));
  if (max_year != null) searchParams.set("max_year", String(max_year));
  if (min_mileage != null) searchParams.set("min_mileage", String(min_mileage));
  if (max_mileage != null) searchParams.set("max_mileage", String(max_mileage));
  if (min_price != null) searchParams.set("min_price", String(min_price));
  if (max_price != null) searchParams.set("max_price", String(max_price));
  if (radius != null) searchParams.set("radius", String(radius));
  if (hide_viewed) searchParams.set("hide_viewed", "true");

  return axios
    .get(`${BASE_URL}/private-listings/paginated?${searchParams.toString()}`, {
      headers: getAuthHeaders(),
    })
    .then((res) => res.data);
};

/** @deprecated Use fetchPrivateListingsPaginated for Buy Signals. Kept for condition report or legacy. */
export const fetchRecommendationsListings = () =>
  axios
    .get(`${BASE_URL}/recommendations/listings`, {
      headers: getAuthHeaders(),
    })
    .then((res) => res.data);

/** Fetch condition report PDF for a listing (streams PDF, returns blob). */
export const fetchConditionReportPdf = (listingId) =>
  axios
    .get(`${BASE_URL}/private-listings/listings/${listingId}/condition-report`, {
      headers: getAuthHeaders(),
      responseType: "blob",
    })
    .then((res) => res.data);

/**
 * Get all makes for filter dropdown.
 * GET /private-listings/makes
 * @returns {Promise<string[]>} Array of make names
 */
export const getPrivateListingsMakes = () =>
  axios
    .get(`${BASE_URL}/private-listings/makes`, { headers: getAuthHeaders() })
    .then((res) => {
      const data = res.data;
      if (Array.isArray(data)) return data;
      if (data?.makes && Array.isArray(data.makes)) return data.makes;
      return [];
    });

const DEFAULT_FILTERS_ENDPOINT = `${BASE_URL}/private-listings/default-filters`;

/**
 * Get saved default filters for Buy Signals.
 * GET /private-listings/default-filters
 * @returns {Promise<{ filters: Object }>} { filters: { listing_type?, make?, min_year?, ... } }; filters may be {}
 */
export const getBuySignalsDefaultFilters = () => {
  return axios
    .get(DEFAULT_FILTERS_ENDPOINT, { headers: getAuthHeaders() })
    .then((res) => res.data ?? { filters: {} });
};

/**
 * Save default filters for Buy Signals.
 * PUT /private-listings/default-filters with body: listing_type, make (array), min_year, max_year, min_mileage, max_mileage, sort_by, sort_order.
 * @param {Object} body - { listing_type, make, min_year, max_year, min_mileage, max_mileage, sort_by, sort_order } (all optional)
 * @returns {Promise<void>}
 */
export const saveBuySignalsDefaultFilters = (body) => {
  return axios.put(DEFAULT_FILTERS_ENDPOINT, body, { headers: getAuthHeaders() });
};

/**
 * Clear saved default filters.
 * DELETE /private-listings/default-filters (same path as GET/PUT)
 * @returns {Promise<void>}
 */
export const clearBuySignalsDefaultFilters = () => {
  return axios.delete(DEFAULT_FILTERS_ENDPOINT, { headers: getAuthHeaders() }).then(() => {});
};

/**
 * Mark a listing as viewed (fire-and-forget, idempotent).
 * PATCH /private-listings/{listing_id}/viewed
 */
export const markListingViewed = (listingId) =>
  axios
    .patch(`${BASE_URL}/private-listings/${listingId}/viewed`, null, {
      headers: getAuthHeaders(),
    })
    .catch(() => {});
